import { Link } from "react-router-dom";
import React, { useMemo, forwardRef } from "react";
import {
  format,
  parse,
  differenceInDays,
  isAfter,
  setHours,
  setMinutes,
  setSeconds,
} from "date-fns";
const S3_URL = import.meta.env.VITE_S3_URL;
import default_image from "../../assets/default_img.png";

// Icons
import IconBlog from "../../assets/iconUpdate/blogSvg.svg";
import IconInstagram from "../../assets/iconUpdate/igSvg.svg";
import IconTiktok from "../../assets/iconUpdate/ttSvg.svg";
import IconYoutube from "../../assets/iconUpdate/ytSvg.svg";
import IconBlogNone from "../../assets/icons/naver_none.jpg";
import IconInstagramNone from "../../assets/icons/insta_none.jpg";
import IconTiktokNone from "../../assets/icons/tictok_none.jpg";
import IconYoutubeNone from "../../assets/icons/youtube_none.jpg";
import IconWeu from "../../assets/iconUpdate/weuSvg.svg";

import { useThemeAsset } from "@/Hooks/useThemeAsset";

export const TYPE_MAPPING = {
  blog: 1,
  instagram: 2,
  tiktok: 3,
  youtube: 4,
  all: 5,
};

export const TYPE_ICONS: Record<
  string,
  {
    active: string;
    inactive: string;
    label: string;
    activeColor: string;
  }
> = {
  blog: {
    active: IconBlog,
    inactive: IconBlogNone,
    label: "블로그",
    activeColor: "text-[#04BE19]",
  },
  instagram: {
    active: IconInstagram,
    inactive: IconInstagramNone,
    label: "인스타그램",
    activeColor: "text-[#C73994]",
  },
  tiktok: {
    active: IconTiktok,
    inactive: IconTiktokNone,
    label: "틱톡",
    activeColor: "text-black",
  },
  youtube: {
    active: IconYoutube,
    inactive: IconYoutubeNone,
    label: "유튜브",
    activeColor: "text-[#FF0000]",
  },
  all: {
    active: IconWeu,
    inactive: "",
    label: "위유",
    activeColor: "text-[#000000]",
  },
};

export type Campaign = {
  id: number;
  title: string;
  thumbnail: string;
  expired_in_days: number;
  type: string;
  campaign_type: string;
  reward: string;
  total_apply: number;
  registration_end_date: string;
  max_apply: number;
};

type CampaignCardProps = {
  campaign: Campaign;
  index?: number;
};

export const CampaignCard = forwardRef<HTMLAnchorElement, CampaignCardProps>(
  ({ campaign, index }, ref) => {
    const { typeIcons } = useThemeAsset();

    // 등록 마감일 및 D-day 계산 로직을 useMemo로 최적화
    const { daysLeft, isClosed } = useMemo(() => {
      try {
        let endDateObj;

        // 날짜 형식 처리
        if (campaign.registration_end_date.includes("-")) {
          const dateParts = campaign.registration_end_date
            .split("-")[0]
            .split("/");
          if (dateParts.length === 3) {
            try {
              endDateObj = parse(
                campaign.registration_end_date,
                "dd/MM/yyyy-HH:mm:ss",
                new Date()
              );
            } catch (parseError) {
              try {
                endDateObj = parse(
                  campaign.registration_end_date,
                  "MM/dd/yyyy-HH:mm:ss",
                  new Date()
                );
              } catch (secondParseError) {
                const [datePart] = campaign.registration_end_date.split("-");
                const [day, month, year] = datePart.split("/").map(Number);
                endDateObj = new Date(year, month - 1, day);
              }
            }
          } else {
            endDateObj = new Date(campaign.registration_end_date);
          }
        } else {
          endDateObj = new Date(campaign.registration_end_date);
        }

        // 시간을 23:59:59로 설정
        const endDateWithTime = setHours(
          setMinutes(setSeconds(endDateObj, 59), 59),
          23
        );

        // 현재 시간 (시작점)
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // 남은 일수 계산
        const dayDiff = differenceInDays(endDateWithTime, today);

        // 마감 여부 (현재 시간이 마감 시간을 지났는지)
        const closed = !isAfter(endDateWithTime, new Date());

        return {
          daysLeft: dayDiff,
          isClosed: closed,
        };
      } catch (error) {
        // 오류 발생 시 기본값 반환
        return {
          daysLeft: campaign.expired_in_days,
          isClosed: campaign.expired_in_days <= 0,
        };
      }
    }, [campaign.registration_end_date, campaign.expired_in_days]);

    return (
      <Link to={`/detailPage/${campaign.id}`} ref={ref}>
        <div className="flex flex-col w-full gap-1">
          <div className="w-full overflow-hidden rounded-2xl object-cover aspect-square relative">
            {isClosed && (
              <div className="absolute top-0 left-0 w-full h-full bg-black/50 flex items-center justify-center">
                <div className="text-white text-center">
                  <p className="text-white text-center">
                    마감 <br />
                    CLOSED
                  </p>
                </div>
              </div>
            )}
            <img
              src={
                campaign.thumbnail
                  ? `${S3_URL}${campaign.thumbnail}`
                  : default_image
              }
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).src = default_image;
              }}
              alt={campaign.title}
              className="object-cover w-full h-full hover:scale-110 transition-all duration-300 border border-gray-300"
            />
          </div>
          <div className="flex gap-1 items-center">
            <div>
              <img
                src={
                  typeIcons[campaign.type]?.active ||
                  TYPE_ICONS[campaign.type]?.active
                }
                alt={campaign.type}
                className="w-[16px] h-[16px] rounded-full"
              />
            </div>
            <div className="text-[8px] h-[16px] leading-[16px] px-1 rounded-full bg-[#eeeeee] text-[#666666] font-semibold ">
              {campaign.campaign_type}
            </div>
            <div className="text-[8px]">
              {isClosed
                ? "발표마감"
                : daysLeft > 0
                ? `${daysLeft}일 남음`
                : daysLeft === 0
                ? "마감임박"
                : "발표마감"}
            </div>
          </div>
          <div className="font-bold text-[12px] flex-1 line-clamp-1">
            {campaign.title}
          </div>
          <p className="w-full line-clamp-2 text-[9px] text-[#999999]">
            {campaign.reward
              .replace(/<[^>]*>?/g, "")
              .replace(/&nbsp;|&amp;nbsp;/g, " ")}
          </p>
          <div
            className={`${
              location.pathname === "/mypage"
                ? "hidden"
                : "flex justify-between"
            }`}
          >
            <div className="flex text-[9px]  gap-1">
              <span className="text-black font-semibold">
                신청 {Number(campaign.total_apply).toLocaleString()}
              </span>
              <span className="text-[#999999]">/</span>
              <span className="text-[#999999] ">
                모집 {Number(campaign.max_apply).toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      </Link>
    );
  }
);

CampaignCard.displayName = "CampaignCard";
